/**
 * useNotificationTemplates — ViewModel for /preferences/email-templates.
 *
 * Composes the shared usePaginatedList<T>. Update mutation hits
 * PATCH /preferences/email-templates/:code. No create / delete —
 * the catalogue of templates is system-managed (one row per
 * notification kind, seeded by migration 0030). Renamed from
 * /iam/notification-templates 2026-05-19 when the surface moved to
 * the new preferences worker.
 */

import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type {
  NotificationTemplateDto,
  UpdateNotificationTemplateInput
} from '../models/notification-template.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

// Single-source-of-truth: src/models/notification-template.model.ts.
// Per SOP 02 §"Mutation Patterns (LOCKED)" — DTO source rule.
export type {
  NotificationTemplateDto,
  UpdateNotificationTemplateInput
} from '../models/notification-template.model'

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: NotificationTemplateDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
  }
}

export interface UseNotificationTemplatesReturn {
  readonly items: NotificationTemplateDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly update: (
    code: string,
    patch: UpdateNotificationTemplateInput
  ) => Promise<
    { ok: true; data: NotificationTemplateDto }
    | { ok: false; field: string | null; message: string }
  >
}

export function useNotificationTemplates(): UseNotificationTemplatesReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<NotificationTemplateDto, undefined>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.PREFERENCESEMAILTEMPLATES}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return { items: body.data.items, total: body.data.total }
      },
      [authedFetch]
    )
  })

  const update = useCallback(
    async (code: string, patch: UpdateNotificationTemplateInput) => {
      const r = await mutateJson<NotificationTemplateDto>(
        authedFetch,
        fillPath(APIROUTES.PREFERENCESEMAILTEMPLATEITEM, { code }),
        'PATCH',
        patch
      )
      if (!r.ok) return r
      paged.updateItem((it) => it.code === code, r.data)
      return { ok: true as const, data: r.data }
    },
    [authedFetch, paged]
  )

  return {
    items: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    update
  }
}

/**
 * Convenience single-template loader for the edit page. Reads from the
 * paginated list (the list returns every template — there's only a
 * handful — so we just filter in-memory rather than a 2nd endpoint).
 */
export function findTemplateByCode(
  items: ReadonlyArray<NotificationTemplateDto>,
  code: string
): NotificationTemplateDto | null {
  return items.find((t) => t.code === code) ?? null
}
